import { FC, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { PageSpinner } from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import { useOptionalMonolithCurrentUser } from 'src/hooks/use-monolith-user';
import { useTitle } from 'src/hooks/use-title';
import { useBalancingCodes } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-balancing-codes';
import { useChannelConfig } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-channel-config';
import { useRemitInfo } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-remit-info';
import { useSecondaryConfig } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-secondary-config';
import {
  TradeProposalRequest,
  useTradeProposal,
} from 'src/pages/reporting/secondary-tradings/trade-proposal/use-trade-proposal';
import { useTraderLists } from 'src/pages/reporting/secondary-tradings/trade-proposal/use-trader-lists';

const Page = lazy(() => import('./page'));

export const ReportingSecondaryTradeProposal: FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  useTitle(`Proposal ID ${proposalId}`);
  const auth = useOptionalMonolithCurrentUser();

  const tradeProposal = useTradeProposal({ proposalId });

  if (!tradeProposal.response) return <PageSpinner />;

  if (auth?.role === 'SHIPPER_USER' || auth?.role === 'SHIPPER_ADMIN') {
    return <LoadShipperResources tradeProposal={tradeProposal} />;
  } else {
    return <Page tradeProposal={tradeProposal} />;
  }
};

const LoadShipperResources: FC<{
  tradeProposal: Successful<TradeProposalRequest>;
}> = ({ tradeProposal }) => {
  const proposal = tradeProposal.response.data;

  const secondaryConfig = useSecondaryConfig({
    networkPointId: proposal.networkPoint.id,
  });
  const balancingCodes = useBalancingCodes({ proposal });
  const traderLists = useTraderLists();
  const channelConfig = useChannelConfig();
  const remitInfo = useRemitInfo();

  if (
    !secondaryConfig.response ||
    !channelConfig.response ||
    !balancingCodes.response ||
    !remitInfo.response ||
    !traderLists.response
  )
    return <PageSpinner />;

  return (
    <Page
      tradeProposal={tradeProposal}
      secondaryConfig={secondaryConfig.response.data}
      balancingCodes={balancingCodes.response.data}
      traderLists={traderLists.response.data}
      channelConfig={channelConfig.response.data}
      remitInfo={remitInfo.response.data}
    />
  );
};
